<script>
import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
} from '@inscribe/signal';
import DocumentRightDataBox from '@/V2/documents/document-view/DocumentRightDataBox';
import CheckDetails from '@/V2/documents/document-view/checks/CheckDetails.vue';
import {
  COMPLIANCE_RESULT,
  VERIFICATION_RESULT_SORT_ORDER,
  ANALYST_GROUPS,
} from '@/constants/analyst-constants';
import { ANALYST_GROUPS_CONFIG } from '@/constants/analyst-config';
import InfoIcon from '@/V2/components/InfoIcon';
import { getCheckSubtitle } from '@/utils/checks';

export default {
  components: {
    CheckDetails,
    DocumentRightDataBox,
    iAccordion,
    iAccordionItem,
    InfoIcon,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    currentFullGroup: {
      type: String,
    },
    currentVerificationComponent: {
      type: String,
    },
    currentVerification: {
      type: Object,
    },
    lastRun: {
      type: Object,
    },
    showLoadingState: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iAccordionItemSizes,
      viewFullGroup: false,
    };
  },
  watch: {
    currentFullGroup(newVal) {
      if (newVal === 'requirements') {
        this.openFullGroup();
      } else {
        this.viewFullGroup = false;
      }
    },
  },
  computed: {
    requirementsGroup() {
      const checksCopy = [
        ...(this.lastRun?.output?.visual_checks || []),
        ...this.document.checks,
      ];
      checksCopy.sort((c1, c2) => (c1.passed > c2.passed ? 1 : -1));
      // group requirements by passed
      const riskLevels = [];
      checksCopy.forEach((check) => {
        const checkPassed =
          check.passed === true || check.result === COMPLIANCE_RESULT.PASS;
        const key = checkPassed ? 'Passed' : 'Failed';
        let foundRiskLevel = riskLevels.find((level) => level.key === key);
        if (!foundRiskLevel) {
          foundRiskLevel = {
            key,
            verifications: [],
          };
          riskLevels.push(foundRiskLevel);
        }
        foundRiskLevel.verifications.push({
          ...check,
          title: check.name || check.title,
          subtitle: check.rationale || getCheckSubtitle(check),
          detailsType: 'requirements',
          result: key,
          passed: checkPassed,
        });
      });

      const failedGroup = riskLevels.find((level) => level.key === 'Failed');
      const failedCount = failedGroup?.verifications.length || 'No';

      // sort
      riskLevels.sort((r1, r2) => {
        return VERIFICATION_RESULT_SORT_ORDER[r1.key] >
          VERIFICATION_RESULT_SORT_ORDER[r2.key]
          ? 1
          : -1;
      });

      const subtitle = `${failedCount} failed ${
        failedCount === 1 ? 'requirement' : 'requirements'
      }`;

      this.$emit('update:subtitle', subtitle);

      return {
        key: ANALYST_GROUPS.REQUIREMENTS,
        label: ANALYST_GROUPS_CONFIG[ANALYST_GROUPS.REQUIREMENTS].label,
        riskLevels,
        subtitle,
      };
    },
  },
  methods: {
    openFullGroup() {
      this.viewFullGroup = true;
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.documentReviewGroupRequirements);
      });
    },
    openItemDetails(requirement) {
      this.$emit('set-verification', requirement);
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.checkDetails);
      });
    },
  },
};
</script>

<template>
  <template>
    <!-- Requirements -->
    <i-accordion-item
      :mount-open="true"
      title="Requirements"
      :no-background="true"
      :size="iAccordionItemSizes.LARGE"
      class="half-margin"
      data-testid="requirements-group"
    >
      <!-- title extra content -->
      <template #extra-title-content>
        <info-icon
          class="i-mr-4"
          title="Requirements"
          description="A requirement check examines specific elements such as document type or date to verify compliance with requirements."
          link-text="Learn more"
          link-external-url="https://help.inscribe.ai/en/articles/5677805-checks"
        />
      </template>

      <!-- requirements group content -->
      <document-right-data-box
        :group="requirementsGroup"
        :compact-version="true"
        empty-state-title="No requirements detected"
        empty-state-subtitle="Document requirements will appear here"
        empty-state-label="Manage Requirements"
        empty-state-icon="medium_file_alert_1"
        :show-loading-state="false"
        @show-item-details="openItemDetails"
        @view-all="openFullGroup(requirementsGroup)"
        @empty-state-btn-click="
          $emit('open-decision-engine', 'decision-engine-checks')
        "
      ></document-right-data-box>
    </i-accordion-item>

    <!-- check requirements full group -->
    <document-right-data-box
      v-if="viewFullGroup"
      :ref="`documentReviewGroupRequirements`"
      :is-loading="showLoadingState"
      :group="requirementsGroup"
      :compact-version="false"
      class="verification-group"
      empty-state-title="No requirements detected"
      empty-state-subtitle="Document requirements will appear here"
      empty-state-label="Manage Requirements"
      empty-state-icon="medium_file_alert_1"
      :show-loading-state="false"
      @show-item-details="openItemDetails"
      @empty-state-btn-click="
        $emit('open-decision-engine', 'decision-engine-checks')
      "
    ></document-right-data-box>

    <!-- legacy verification details -->
    <check-details
      v-if="currentVerification?.detailsType === 'requirements'"
      ref="checkDetails"
      :check="currentVerification"
    ></check-details>
  </template>
</template>
